import { DoubleRightOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
  Alert,
  DatePicker,
  InputNumber,
  message,
  Modal,
  ModalProps,
  Select,
  Switch,
  Tooltip,
  Typography,
} from 'antd';
import moment, { Moment } from 'moment';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQueryClient } from 'react-query';

import useAPIQuery from '../../api/useAPIQuery';
import useInfiniteAPIQuery from '../../api/useInfiniteAPIQuery';
import usePrefetchPages from '../../hooks/usePrefetchPages';
import {
  CandidateDetail,
  JobPostingDetail,
  PaginatedResponse,
  RecruitmentProcessCollection,
  VixtingJobPostingCollection,
} from '../../types';
import { mapVixtingCompany, mapVixtingDepartment, mapVixtingJobRole } from '../../utils';
import Form from '../Form';
import SankhyaAdmissionForm from '../SankhyaAdmissionForm';
import VixtingAdmissionForm from '../VixtingAdmissionForm';
import ActiveCandidatesWarningModal from './ActiveCandidatesWarningModal';
import ConfirmHireCandidateModal from './ConfirmHireCandidateModal';
import MissingAdmissionDataAlert from './MissingAdmissionDataAlert';

export type MoveCandidateStageFormValues = {
  stageId: string;
  admissionDate?: Moment;
  salary?: number;
} & (
  | {
      // Integration Form Values
      sendIntegrationAdmission: false;
      candidateEmail: never;
      candidateCPF: never;
      sankhyaJobRole: never;
      sankhyaCompany: never;
      sankhyaDepartment: never;
      affiliate: never;
      unitBusiness: never;
      admissionType?: never;
      vixtingLimitDate?: never;
    }
  | {
      // Admission Integration Form Values
      sendIntegrationAdmission: true;
      candidateEmail: string;
      candidateCPF: string;
      sankhyaJobRole: string;
      sankhyaCompany: string;
      sankhyaDepartment: string;
      affiliate?: string;
      unitBusiness?: string;
      admissionType?: string;
      vixtingLimitDate?: Moment;
    }
);

export interface MoveCandidateStageModalProps extends ModalProps {
  jobPostingId: JobPostingDetail['id'];
  loading?: boolean;
  helmetTitle: string;
  onFinish?: (values: MoveCandidateStageFormValues) => void;
  candidate?: CandidateDetail;
  recruitmentProcess?: RecruitmentProcessCollection;
  isHiredStageDisabled?: boolean;
}

const STALE_TIME = 1000 * 60 * 5;

const MoveCandidateStageModal = ({
  jobPostingId,
  loading,
  helmetTitle,
  onFinish,
  candidate,
  recruitmentProcess,
  isHiredStageDisabled,
  ...props
}: MoveCandidateStageModalProps) => {
  const [form] = Form.useForm();
  const sendIntegrationAdmissionValue = Form.useWatch('sendIntegrationAdmission', form);

  const config = useAPIQuery('/configs/tenant-configurations/');

  const queryClient = useQueryClient();

  const jobPosting = useAPIQuery([`/job-postings/${jobPostingId}/`]);

  const [confirmHireModalIsOpen, setConfirmHireModalIsOpen] = useState<boolean>(false);
  const [activeCandidatesWarningModalIsOpen, setActiveCandidatesWarningModalIsOpen] =
    useState<boolean>(false);

  let remainingPositions = -1;
  if (
    typeof jobPosting.data?.positions === 'number' &&
    typeof jobPosting.data?.filled_positions === 'number'
  ) {
    remainingPositions = jobPosting.data?.positions - jobPosting.data?.filled_positions;
  }

  const startSalaryRange = jobPosting.data?.start_salary_range;
  const endSalaryRange = jobPosting.data?.end_salary_range;

  const stagesPages = useInfiniteAPIQuery([
    '/stages/',
    {
      params: {
        job_posting_id: jobPostingId,
        type__in: 'SELECTED, CUSTOM, APPROVED, HIRED',
      },
    },
  ]);
  const stages = usePrefetchPages(stagesPages, (data) => data.results);

  const stageIdValue = Form.useWatch('stageId', form);
  const stage = useAPIQuery([`/stages/${stageIdValue}/`], {
    enabled: !!stageIdValue,
  });
  const isHiredStage = stage.data?.type === 'HIRED';

  const integration: 'sankhya' | 'vixting' = config.data?.enableVixtingAdmission
    ? 'vixting'
    : 'sankhya';

  const sankhyaJobPosting = useAPIQuery(
    [
      '/sankhya/sankhya-job-postings/',
      {
        params: {
          job_posting_id: jobPostingId,
        },
      },
    ],
    {
      enabled: !!config.data?.enableSankhyaAdmission,
      select(data) {
        return data.count > 0 ? data.results[0] : undefined;
      },
    }
  );

  const vixtingJobPosting = useAPIQuery(
    [
      '/vixting/vixting-job-postings/',
      {
        params: {
          job_posting_id: jobPostingId,
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission,
      select(data) {
        return data.count > 0 ? data.results[0] : undefined;
      },
    }
  );
  const integrationJobPosting = integration === 'vixting' ? vixtingJobPosting : sankhyaJobPosting;

  const isVixtingFormAndIsMissingData =
    sendIntegrationAdmissionValue &&
    config.data?.enableVixtingAdmission &&
    (!integrationJobPosting.data?.company ||
      !integrationJobPosting.data?.job_role ||
      !integrationJobPosting.data?.department ||
      !(integrationJobPosting.data as VixtingJobPostingCollection)?.affiliate);

  const sankhyaCompaniesPages = useInfiniteAPIQuery(
    [
      `/sankhya/companies/`,
      {
        params: {
          offsetPage: '0',
          fieldsetList: 'CODEMP',
          joinFieldsetList: 'NOMEFANTASIA',
          sort: 'CODEMP',
        },
      },
    ],
    {
      enabled: !!config.data?.enableSankhyaAdmission,
      pageKey: 'offsetPage',
    }
  );
  const sankhyaCompanies = usePrefetchPages(sankhyaCompaniesPages, (data) => data.results);

  const sankhyaJobRolesPages = useInfiniteAPIQuery(
    [
      `/sankhya/job-roles/`,
      {
        params: {
          fieldsetList: 'CODCARGO,DESCRCARGO',
          filter: {
            ATIVO: 'S',
          },
          sort: 'CODCARGO',
        },
      },
    ],
    {
      enabled: !!config.data?.enableSankhyaAdmission,
      pageKey: 'offsetPage',
    }
  );
  const sankhyaJobRoles = usePrefetchPages(sankhyaJobRolesPages, (data) => data.results);

  const sankhyaDepartmentsPages = useInfiniteAPIQuery(
    [
      `/sankhya/departments/`,
      {
        params: {
          fieldsetList: 'CODDEP,DESCRDEP',
          filter: {
            ATIVO: 'S',
            ANALITICO: 'S',
          },
          sort: 'CODDEP',
        },
      },
    ],
    {
      enabled: !!config.data?.enableSankhyaAdmission,
      pageKey: 'offsetPage',
    }
  );
  const sankhyaDepartments = usePrefetchPages(sankhyaDepartmentsPages, (data) => data.results);

  const vixtingAffiliatesPages = useInfiniteAPIQuery(
    [
      '/vixting/affiliates/',
      {
        params: {
          company_id: integrationJobPosting.data?.company || '',
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission && !!integrationJobPosting.data?.company,
      staleTime: STALE_TIME,
      onSuccess: () => {
        if (
          !(integrationJobPosting.data as VixtingJobPostingCollection)?.affiliate &&
          !!integrationJobPosting.data?.company
        )
          form.setFieldValue('affiliate', integrationJobPosting.data.company);
      },
      onError: () => {
        message.error('Falha ao carregar filiais da vixting!');
      },
      pageKey: 'offsetPage',
    }
  );

  const vixtingAffiliates = usePrefetchPages(vixtingAffiliatesPages, (data) => data.results);

  const vixtingUnitBusinessesPages = useInfiniteAPIQuery(
    [
      '/vixting/unit-businesses/',
      {
        params: {
          company_id: integrationJobPosting.data?.company || '',
        },
      },
    ],
    {
      enabled:
        !!config.data?.enableVixtingAdmission &&
        !!config.data?.enableVixtingUnitBusiness &&
        !!integrationJobPosting.data?.company,
      staleTime: STALE_TIME,
      onError: () => {
        message.error('Falha ao carregar unidades de negócio da vixting!');
      },
      pageKey: 'offsetPage',
    }
  );

  const vixtingUnitBusinesses = usePrefetchPages(
    vixtingUnitBusinessesPages,
    (data) => data.results
  );

  const vixtingCompaniesPages = useInfiniteAPIQuery(
    [
      '/vixting/vixting-companies/',
      {
        params: {
          is_active: true,
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission,
      staleTime: STALE_TIME,
      onError: () => {
        message.error('Falha ao carregar empresas!');
      },
    }
  );
  const vixtingCompanies = usePrefetchPages(vixtingCompaniesPages, (data) => data.results);

  const vixtingJobRolesPages = useInfiniteAPIQuery(
    [
      '/vixting/job-roles/',
      {
        params: {
          company_id: integrationJobPosting.data?.company || '',
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission && !!integrationJobPosting.data?.company,
      staleTime: STALE_TIME,
      onError: () => {
        message.error('Falha ao carregar cargos da vixting!');
      },
      pageKey: 'offsetPage',
    }
  );
  const vixtingJobRoles = usePrefetchPages(vixtingJobRolesPages, (data) => data.results);

  const vixtingDepartmentsPages = useInfiniteAPIQuery(
    [
      '/vixting/departments/',
      {
        params: {
          company_id: integrationJobPosting.data?.company || '',
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission && !!integrationJobPosting.data?.company,
      staleTime: STALE_TIME,
      onError: () => {
        message.error('Falha ao carregar departamentos da vixting!');
      },
      pageKey: 'offsetPage',
    }
  );
  const vixtingDepartments = usePrefetchPages(vixtingDepartmentsPages, (data) => data.results);

  const formArgs = {
    enableSankhyaAdmission:
      config.data?.enableSankhyaAdmission || config.data?.enableVixtingAdmission,
    sankhyaCompanies: config.data?.enableVixtingAdmission
      ? mapVixtingCompany(vixtingCompanies)
      : sankhyaCompanies,
    sankhyaJobRoles: config.data?.enableVixtingAdmission
      ? mapVixtingJobRole(vixtingJobRoles)
      : sankhyaJobRoles,
    sankhyaDepartments: config.data?.enableVixtingAdmission
      ? mapVixtingDepartment(vixtingDepartments)
      : sankhyaDepartments,
    affiliates: config.data?.enableVixtingAdmission ? vixtingAffiliates : [],
    unitBusinesses: config.data?.enableVixtingAdmission ? vixtingUnitBusinesses : [],
  };

  const getVixtingLimitDate = (admissionDate: Moment | null) => {
    return admissionDate && config.data?.vixtingAdmissionDateReference === 'admission_date'
      ? moment(admissionDate).subtract(config.data?.vixtingDaysToAdmission, 'days').startOf('day')
      : moment().add(config.data?.vixtingDaysToAdmission, 'days').startOf('day');
  };
  const resetVixtingLimitDateFields = async (
    admissionDate: Moment | null,
    _admissionDateString: string | null
  ) => {
    config.data?.enableVixtingAdmission &&
      form.setFieldsValue({
        vixtingLimitDate: getVixtingLimitDate(admissionDate),
      });
  };
  const admissionDateInitialValue =
    !!config.data?.enableVixtingAdmission &&
    config.data?.vixtingAdmissionDateReference === 'admission_date'
      ? moment().add(config.data?.vixtingDaysToAdmission, 'days').startOf('day')
      : moment().startOf('day');
  const defaultCandidatePhone =
    candidate && !!candidate.phone_contacts[0] ? candidate.phone_contacts[0].number : 'NA';

  const isVacancyCompanyUsable =
    integration === 'sankhya' ||
    (integration === 'vixting' &&
      vixtingCompanies.some(
        (company) => company.id === integrationJobPosting.data?.company && company.is_active
      ));

  return (
    <>
      <ConfirmHireCandidateModal
        key={`ConfirmHireCandidateModal-${candidate?.id}`}
        open={confirmHireModalIsOpen}
        onOk={
          remainingPositions < 1
            ? () => setConfirmHireModalIsOpen(false)
            : () => {
                setConfirmHireModalIsOpen(false);
                form.submit();
              }
        }
        onCancel={() => setConfirmHireModalIsOpen(false)}
        onSuccess={() => {
          setConfirmHireModalIsOpen(false);
        }}
        remainingPositions={remainingPositions}
        jobPostingName={jobPosting.data?.name || ''}
        candidateName={candidate?.name || ''}
        destroyOnClose
      />
      <ActiveCandidatesWarningModal
        open={activeCandidatesWarningModalIsOpen}
        onOk={() => {
          setConfirmHireModalIsOpen(true);
          setActiveCandidatesWarningModalIsOpen(false);
        }}
        onCancel={() => setActiveCandidatesWarningModalIsOpen(false)}
        destroyOnClose
      />
      <Modal
        okButtonProps={{
          htmlType: 'submit',
          icon: <DoubleRightOutlined />,
          loading,
          disabled: isVixtingFormAndIsMissingData,
        }}
        okText="Mover"
        onOk={async () => {
          if (!isHiredStage) {
            return form.submit();
          }

          if (remainingPositions === 1) {
            const activeRecruitmentProcesses = await queryClient.fetchQuery<
              PaginatedResponse<RecruitmentProcessCollection>
            >([
              '/recruitment-processes/',
              {
                params: {
                  job_posting: jobPostingId,
                  status: 'ACTIVE',
                  stage__type: 'HIRED',
                },
              },
            ]);

            if (activeRecruitmentProcesses && activeRecruitmentProcesses.count > 1) {
              await form.validateFields();
              return setActiveCandidatesWarningModalIsOpen(true);
            }
          }

          if (remainingPositions <= 1) {
            await form.validateFields();
            return setConfirmHireModalIsOpen(true);
          }

          return form.submit();
        }}
        {...props}
      >
        <Helmet>
          <title>{helmetTitle} | Recrutamento e Seleção</title>
        </Helmet>
        <Form<MoveCandidateStageFormValues>
          form={form}
          disabled={isVixtingFormAndIsMissingData}
          layout="vertical"
          initialValues={{
            stageId: '',
            admissionDate: admissionDateInitialValue,
            // Admission Integration Form Values
            sendIntegrationAdmission: false,
            candidateEmail: candidate?.email || '',
            candidateCPF: candidate?.cpf || '',
            sankhyaCompany: integrationJobPosting.data?.company,
            sankhyaJobRole: integrationJobPosting.data?.job_role,
            sankhyaDepartment: integrationJobPosting.data?.department,
            affiliate: (integrationJobPosting.data as VixtingJobPostingCollection)?.affiliate,
            unitBusiness: (integrationJobPosting.data as VixtingJobPostingCollection)
              ?.unit_business,
            vixtingLimitDate: getVixtingLimitDate(admissionDateInitialValue),
            candidatePhone: defaultCandidatePhone,
          }}
          onFinish={onFinish}
        >
          {isHiredStage && (
            <Form.Item>
              <Alert
                message={
                  <Typography.Text>
                    <Typography.Text strong>Atenção:</Typography.Text> Ao movimentar este
                    candidato(a) para a etapa de "contratados" a ação não poderá ser desfeita.
                  </Typography.Text>
                }
                type="warning"
                showIcon
              />
            </Form.Item>
          )}
          {isVixtingFormAndIsMissingData && (
            <MissingAdmissionDataAlert jobPostingId={jobPostingId} />
          )}
          <Form.Item label="Mover para a etapa" name="stageId" rules={[{ required: true }]}>
            <Select data-testid="select-stage">
              {stages.map((stage) => {
                if (stage.type === 'HIRED' && isHiredStageDisabled) {
                  return (
                    <Select.Option key={stage.id} value={stage.id} disabled>
                      <Tooltip title="Não é possível movimentar candidatos em massa para a etapa de contratados, faça a ação manualmente.">
                        <div style={{ width: '100%' }}>{stage.name}</div>
                      </Tooltip>
                    </Select.Option>
                  );
                }
                return (
                  <Select.Option
                    key={stage.id}
                    value={stage.id}
                    disabled={recruitmentProcess?.stage.id === stage.id}
                  >
                    {stage.name}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item hidden={!isHiredStage}>
            <Form.Row>
              <Form.Col span={12}>
                <Form.Item
                  label="Data de admissão"
                  name="admissionDate"
                  rules={[
                    {
                      required: isHiredStage,
                    },
                  ]}
                >
                  <DatePicker
                    disabledDate={(current) => {
                      // Days before today are not allowed
                      return current && current < moment().startOf('day');
                    }}
                    onChange={resetVixtingLimitDateFields}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Form.Col>
              <Form.Col span={12}>
                <Form.Item
                  label={
                    <Typography.Text>
                      Salário{' '}
                      <Tooltip
                        title={
                          endSalaryRange
                            ? startSalaryRange
                              ? `Faixa salarial definida entre R$ ${startSalaryRange
                                  .toString()
                                  .replace(/\B(?=(\d{3})+(?!\d))/g, '.')} e R$ ${endSalaryRange
                                  .toString()
                                  .replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`
                              : `Salário máximo definido de R$ ${endSalaryRange
                                  .toString()
                                  .replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`
                            : 'Vaga sem informação de Faixa Salarial'
                        }
                      >
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Typography.Text>
                  }
                  name="salary"
                  rules={[
                    {
                      required: isHiredStage && !!endSalaryRange,
                      message: 'Por favor, insira o salário.',
                    },
                    {
                      validator(rule, value) {
                        const doesntHaveSalaryRange = !endSalaryRange && !startSalaryRange;
                        const hasEndRangeOnly = endSalaryRange && !startSalaryRange;
                        const hasBothRanges = endSalaryRange && startSalaryRange;

                        if (value !== null && value !== undefined) {
                          if (!Number.isSafeInteger(value)) {
                            return Promise.reject(new Error('Insira um valor numérico'));
                          }

                          if (value <= 0 && (doesntHaveSalaryRange || hasEndRangeOnly)) {
                            return Promise.reject(new Error('O salário deve ser maior que 0'));
                          }

                          if (
                            hasBothRanges &&
                            (value <= 0 || value > endSalaryRange || value < startSalaryRange)
                          ) {
                            return Promise.reject(
                              new Error(
                                `O salário deve estar entre R$ ${startSalaryRange
                                  .toString()
                                  .replace(/\B(?=(\d{3})+(?!\d))/g, '.')} e R$ ${endSalaryRange
                                  .toString()
                                  .replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`
                              )
                            );
                          }

                          if (endSalaryRange && value > endSalaryRange) {
                            return Promise.reject(
                              new Error(
                                `O salário deve ser menor que R$ ${endSalaryRange
                                  .toString()
                                  .replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`
                              )
                            );
                          }
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber<string | number>
                    prefix="R$"
                    style={{ width: '100%' }}
                    step={100}
                    min={0}
                    max={2147483647}
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                    parser={(displayValue) => {
                      if (!displayValue) {
                        return '';
                      }
                      return parseInt(displayValue?.replace(/\$\s?|(\.*)/g, '') || '0', 10);
                    }}
                  />
                </Form.Item>
              </Form.Col>
            </Form.Row>
          </Form.Item>
          <Form.Item
            hidden={
              !(
                (config.data?.enableSankhyaAdmission || config.data?.enableVixtingAdmission) &&
                isHiredStage &&
                candidate
              )
            }
            noStyle
          >
            <Tooltip
              title={
                !isVacancyCompanyUsable
                  ? 'Alguns dados obrigatórios da vaga não foram preenchidos. Para prosseguir com o processo de admissão da Vixting, complete as informações na tela de edição da vaga.'
                  : ''
              }
            >
              <Form.Row align="middle">
                <Form.Col>
                  <Form.Item name="sendIntegrationAdmission" valuePropName="checked" noStyle>
                    <Switch
                      data-testid="sendIntegrationAdmission_switch"
                      disabled={!isVacancyCompanyUsable}
                    />
                  </Form.Item>
                </Form.Col>
                <Form.Col>
                  <Typography.Text>
                    Enviar para o processo de admissão da{' '}
                    {config.data?.enableVixtingAdmission ? 'Vixting' : 'Sankhya'}
                  </Typography.Text>
                </Form.Col>
              </Form.Row>
            </Tooltip>
            {sendIntegrationAdmissionValue && (
              <>
                <br />
                {config.data?.enableVixtingAdmission ? (
                  <VixtingAdmissionForm {...formArgs} />
                ) : (
                  <SankhyaAdmissionForm
                    sankhyaJobPosting={integrationJobPosting.data}
                    {...formArgs}
                  />
                )}
              </>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default MoveCandidateStageModal;
